import React, { useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, X, Pencil, Plus, Search, AlertTriangle, MapPin, ShieldCheck, Trash2, Upload, FileDown, Radio, Link2, UserPlus, Globe, LocateFixed, Star, MessageSquareText, GitBranch } from "lucide-react";
import { FavoriteNote } from "@/components/admin/FavoriteNote";
import { LeaderChurchesBox, AskStatusChip, AskResponsibleDialog, ParentChurchField } from "@/components/admin/ChurchAnnexTools";
import { ChurchForm, emptyChurch, churchFormValid } from "@/components/ChurchForm";
import { Switch } from "@/components/ui/switch";
import { countryList } from "@/lib/countries";
import { useApp } from "@/context/AppContext";
import { liveTitle } from "@/lib/liveTitle";

export const ChurchesPanel = ({ canManage = true, canLink = false, isSuper = false }) => {
  const { config, user, setConfig, t } = useApp();
  const [queue, setQueue] = useState([]);
  const [churches, setChurches] = useState([]);
  const [topShared, setTopShared] = useState([]);
  const [counts, setCounts] = useState({ verified: 0, pending: 0 });
  const [q, setQ] = useState("");
  const [editItem, setEditItem] = useState(null);
  const [busy, setBusy] = useState(false);
  const [importing, setImporting] = useState(false);
  const [unattached, setUnattached] = useState([]);
  const [churchOpts, setChurchOpts] = useState([]);
  const [attachSel, setAttachSel] = useState({});
  const [linkItem, setLinkItem] = useState(null);
  const [linkQ, setLinkQ] = useState("");
  const [linkResults, setLinkResults] = useState([]);
  const [linkSel, setLinkSel] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const fileRef = useRef(null);
  const countries = countryList("fr");
  const [osmOpen, setOsmOpen] = useState(false);
  const [osmZone, setOsmZone] = useState("");
  const [osmCountry, setOsmCountry] = useState("");
  const [osmBusy, setOsmBusy] = useState(false);
  const [osmImporting, setOsmImporting] = useState(false);
  const [osmRes, setOsmRes] = useState(null);
  const [osmSel, setOsmSel] = useState({});
  const [liveSearch, setLiveSearch] = useState("");
  const [radiusKm, setRadiusKm] = useState(String(config?.settings?.church_radius_km || 25));
  const [radiusBusy, setRadiusBusy] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [qPage, setQPage] = useState(1);
  const [qPages, setQPages] = useState(1);
  const [qTotal, setQTotal] = useState(0);
  const [osmTotal, setOsmTotal] = useState(0);
  const [favIds, setFavIds] = useState(new Set());
  const [favNotes, setFavNotes] = useState({});
  const [favOnly, setFavOnly] = useState(false);
  const [favCount, setFavCount] = useState(0);
  const [askItem, setAskItem] = useState(null); // lot 631 — question au responsable
  const favOnlyRef = useRef(false);
  const toggleFav = async (c) => {
    try {
      const { data } = await api.post(`/admin/churches/${c.id}/favorite`);
      setFavIds((s) => { const n = new Set(s); if (data.favorite) n.add(c.id); else n.delete(c.id); return n; });
      setFavNotes((m) => { const n = { ...m }; delete n[c.id]; return n; });
      setFavCount(data.count || 0);
      setChurches((arr) => (favOnlyRef.current && !data.favorite ? arr.filter((x) => x.id !== c.id) : arr.map((x) => (x.id === c.id ? { ...x, is_favorite: data.favorite, favorite_note: "" } : x))));
      toast.success(data.favorite ? `${c.name} ajoutée à tes favoris ★` : `${c.name} retirée de tes favoris`);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const loadQueue = (p = 1) => api.get(`/admin/churches/queue?page=${p}&limit=30`).then(({ data }) => {
    setQueue(data.items || []);
    setQPage(data.page || 1);
    setQPages(data.pages || 1);
    setQTotal(data.total || 0);
    setOsmTotal(data.osm_pending_total || 0);
  }).catch(() => {});
  const loadUnattached = (query) => api.get(`/admin/lives/unattached${query ? `?q=${encodeURIComponent(query)}` : ""}`).then(({ data }) => setUnattached(data.lives || [])).catch(() => {});
  const loadList = useCallback((query, p = 1, onlyFav = favOnlyRef.current) => {
    api.get(`/admin/churches?page=${p}&limit=50${query ? `&q=${encodeURIComponent(query)}` : ""}${onlyFav ? "&favorites_only=true" : ""}`)
      .then(({ data }) => {
        setChurches(data.churches || []);
        setCounts({ verified: data.total_verified || 0, pending: data.total_pending || 0 });
        setPage(data.page || 1);
        setPages(data.pages || 1);
        setTotal(data.total || 0);
        setFavIds(new Set((data.churches || []).filter((c) => c.is_favorite).map((c) => c.id)));
        setFavNotes(Object.fromEntries((data.churches || []).filter((c) => c.is_favorite && c.favorite_note).map((c) => [c.id, c.favorite_note])));
        setFavCount(data.favorites_total || 0);
      })
      .catch(() => {});
  }, []);
  const setFavFilter = (on) => { favOnlyRef.current = on; setFavOnly(on); loadList(q, 1, on); };
  useEffect(() => {
    loadQueue(); loadList("");
    if (canManage) {
      loadUnattached();
      api.get("/churches").then(({ data }) => setChurchOpts(data.churches || [])).catch(() => {});
    }
  }, [loadList, canManage]);
  useEffect(() => {
    const r = config?.settings?.church_radius_km;
    if (r) setRadiusKm(String(r));
  }, [config]);

  const reloadAll = () => { loadQueue(qPage); loadList(q, page); };
  const saveRadius = async () => {
    const r = parseFloat(radiusKm);
    if (!r || r < 1 || r > 500) { toast.error("Rayon invalide (1 à 500 km)"); return; }
    setRadiusBusy(true);
    try {
      await api.patch("/admin/churches/radius", { radius_km: r });
      toast.success(`Rayon par défaut réglé sur ${r} km ✅`);
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setRadiusBusy(false); }
  };
  const [mapBusy, setMapBusy] = useState(false);
  const mapEnabled = !!config?.settings?.nearby_map_enabled;
  const toggleNearbyMap = async (enabled) => {
    setMapBusy(true);
    try {
      await api.patch("/admin/settings/nearby-map", { enabled });
      setConfig((c) => ({ ...c, settings: { ...(c.settings || {}), nearby_map_enabled: enabled } }));
      toast.success(enabled ? "Carte de proximité activée ✅" : "Carte de proximité désactivée");
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setMapBusy(false); }
  };
  const geocodeMissing = async () => {
    setGeocoding(true);
    try {
      const { data } = await api.post("/admin/churches/geocode-missing");
      if (data.queued) toast.success(`${data.queued} église(s) en cours de géolocalisation en arrière-plan…`);
      else toast.info("Toutes les églises ont déjà des coordonnées GPS.");
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setGeocoding(false); }
  };
  const attachLive = async (liveId) => {
    const churchId = attachSel[liveId];
    if (!churchId) { toast.error("Choisis une église"); return; }
    try { await api.post(`/admin/lives/${liveId}/attach-church`, { church_id: churchId }); toast.success("Direct rattaché à l'église ✅"); loadUnattached(); }
    catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };
  const attachAllFromHost = async (hostId, churchId) => {
    if (!churchId) { toast.error("Choisis d'abord une église sur cette ligne"); return; }
    if (!window.confirm("Rattacher TOUTES les rediffusions non liées de cet hôte à cette église ?")) return;
    try {
      const { data } = await api.post("/admin/lives/attach-all-by-host", { host_id: hostId, church_id: churchId });
      toast.success(`${data.attached} rediffusion(s) rattachée(s) ✅`);
      loadUnattached();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
  };

  const doImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/admin/churches/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Import terminé : ${data.created} ajoutée(s), ${data.skipped} ignorée(s) sur ${data.total}`);
      if ((data.errors || []).length) toast.info(data.errors.slice(0, 3).join(" · "));
      reloadAll();
    } catch (e2) { toast.error(e2?.friendlyMessage || "Import impossible"); }
    finally { setImporting(false); }
  };
  const downloadTemplate = () => {
    const csv = "nom,ville,pays,titre_responsable,nom_responsable,obédience\r\n"
      + "Bethesda Tabernacle,Charleroi,BE,Pasteur,Patrick Kadiata,Évangélique\r\n"
      + "Église Vie Nouvelle,Paris,FR,Pasteur,Jean Dupont,Pentecôtiste\r\n";
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "modele-eglises.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const runOsmSearch = async () => {
    if (osmZone.trim().length < 2) { toast.error("Saisis une zone (ville, région ou pays)"); return; }
    setOsmBusy(true); setOsmRes(null); setOsmSel({});
    try {
      const params = new URLSearchParams({ zone: osmZone.trim() });
      if (osmCountry) params.set("country", osmCountry);
      const { data } = await api.get(`/admin/churches/osm-search?${params.toString()}`);
      setOsmRes(data);
      if (!data.churches?.length) toast.info("Aucune église trouvée dans cette zone.");
    } catch (e) { toast.error(e?.friendlyMessage || "Recherche impossible"); }
    finally { setOsmBusy(false); }
  };
  const osmSelectable = () => (osmRes?.churches || []).filter((c) => !c.already_exists).length;
  const osmSelected = () => Object.values(osmSel).filter(Boolean).length;
  const toggleOsm = (key) => setOsmSel((s) => ({ ...s, [key]: !s[key] }));
  const toggleOsmAll = () => {
    const selectAll = osmSelected() < osmSelectable();
    const sel = {};
    if (selectAll) (osmRes?.churches || []).forEach((c) => { if (!c.already_exists) sel[c.osm_key] = true; });
    setOsmSel(sel);
  };
  const osmMap = (c) => ({ name: c.name, city: c.city, country: c.country, denomination: c.denomination, address: c.address, website: c.website, osm_id: c.osm_id, osm_type: c.osm_type, lat: c.lat, lon: c.lon });
  const doOsmImport = async (chosen) => {
    setOsmImporting(true);
    try {
      const { data } = await api.post("/admin/churches/osm-import", { churches: chosen });
      toast.success(`Import : ${data.created} ajoutée(s) en attente, ${data.skipped} ignorée(s).`);
      setOsmOpen(false); setOsmRes(null); setOsmSel({}); setOsmZone(""); setOsmCountry("");
      reloadAll();
    } catch (e) { toast.error(e?.friendlyMessage || "Import impossible"); }
    finally { setOsmImporting(false); }
  };
  const runOsmImport = async () => {
    const chosen = (osmRes?.churches || []).filter((c) => osmSel[c.osm_key] && !c.already_exists).map(osmMap);
    if (!chosen.length) { toast.error("Coche au moins une église"); return; }
    await doOsmImport(chosen);
  };
  const runOsmImportAll = async () => {
    const chosen = (osmRes?.churches || []).filter((c) => !c.already_exists).map(osmMap);
    if (!chosen.length) { toast.error("Aucune nouvelle église à importer"); return; }
    if (!window.confirm(`Importer les ${chosen.length} nouvelle(s) église(s) trouvée(s) (en attente de validation) ?`)) return;
    await doOsmImport(chosen);
  };

  const verify = async (id) => {
    try { await api.post(`/admin/churches/${id}/verify`); toast.success("Église validée — le membre est notifié"); reloadAll(); }
    catch { toast.error("Erreur"); }
  };
  const [verifyingAll, setVerifyingAll] = useState(false);
  const osmPendingCount = osmTotal || queue.filter((c) => c.source === "openstreetmap").length;
  const verifyAllOsm = async () => {
    if (!osmPendingCount) return;
    if (!window.confirm(`Valider les ${osmPendingCount} église(s) importée(s) depuis OpenStreetMap en attente ?`)) return;
    setVerifyingAll(true);
    try {
      const { data } = await api.post("/admin/churches/verify-all", { source: "openstreetmap" });
      toast.success(`${data.verified} église(s) validée(s) ✅`);
      reloadAll();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setVerifyingAll(false); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Supprimer l'église « ${c.name} » ?`)) return;
    try { await api.delete(`/admin/churches/${c.id}`); toast.success("Église supprimée"); reloadAll(); }
    catch { toast.error("Erreur"); }
  };
  const [demoBusy, setDemoBusy] = useState(false);
  const demoEnabled = !(config?.settings?.demo_churches_disabled);
  const toggleDemoMode = async (enabled) => {
    if (!enabled && !window.confirm("Désactiver les églises de démonstration ? Les exemples actuels seront supprimés et ne seront plus recréés au démarrage. Tes vraies églises ne sont pas touchées.")) return;
    setDemoBusy(true);
    try {
      const { data } = await api.patch("/admin/churches/demo-mode", { enabled });
      setConfig((c) => ({ ...c, settings: { ...(c.settings || {}), demo_churches_disabled: !enabled } }));
      if (!enabled) toast.success(`Églises de démonstration désactivées${data.deleted ? ` — ${data.deleted} supprimée(s)` : ""} ✅`);
      else toast.success("Églises de démonstration réactivées.");
      reloadAll();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setDemoBusy(false); }
  };
  const deleteDemo = async () => {
    if (!window.confirm("Supprimer toutes les églises de démonstration (les exemples pré-remplis) ? Tes vraies églises ne seront pas touchées, et elles ne reviendront plus après un déploiement.")) return;
    setDemoBusy(true);
    try {
      const { data } = await api.post("/admin/churches/delete-demo");
      setConfig((c) => ({ ...c, settings: { ...(c.settings || {}), demo_churches_disabled: true } }));
      if (data.deleted) toast.success(`${data.deleted} église(s) de démonstration supprimée(s) ✅`);
      else toast.info("Aucune église de démonstration trouvée.");
      reloadAll();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setDemoBusy(false); }
  };
  const saveEdit = async () => {
    if (!churchFormValid(editItem)) return toast.error("Nom, ville et pays sont requis");
    setBusy(true);
    try {
      if (editItem.id) await api.patch(`/admin/churches/${editItem.id}`, editItem);
      else await api.post("/admin/churches", { ...editItem, verified: true });
      toast.success(editItem.id ? "Église modifiée" : "Église ajoutée");
      setEditItem(null); reloadAll();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setBusy(false); }
  };
  const openLink = (c) => { setLinkItem(c); setLinkQ(""); setLinkResults([]); setLinkSel(null); };
  const searchPreachers = async (val) => {
    setLinkQ(val); setLinkSel(null);
    if (val.trim().length < 2) { setLinkResults([]); return; }
    try {
      const { data } = await api.get(`/admin/users?q=${encodeURIComponent(val.trim())}&role=preacher&limit=8`);
      setLinkResults(data.users || []);
    } catch { setLinkResults([]); }
  };
  const linkOwner = async () => {
    if (!linkItem || !linkSel?.email) return;
    setLinkBusy(true);
    try {
      const { data } = await api.post(`/admin/churches/${linkItem.id}/link-owner`, { owner_email: linkSel.email });
      toast.success(`Église liée à ${data.owner?.name || linkSel.name} ✅`);
      setLinkItem(null); reloadAll();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setLinkBusy(false); }
  };
  const unlinkOwner = async () => {
    if (!linkItem) return;
    setLinkBusy(true);
    try {
      await api.post(`/admin/churches/${linkItem.id}/link-owner`, { owner_email: "" });
      toast.success("Responsable délié de la page église");
      setLinkItem(null); reloadAll();
    } catch (e) { toast.error(e?.friendlyMessage || "Erreur"); }
    finally { setLinkBusy(false); }
  };

  useEffect(() => {
    let alive = true;
    api.get("/admin/churches/top-shared?limit=8")
      .then(({ data }) => { if (alive) setTopShared(data.churches || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <div className="space-y-4" data-testid="admin-churches-panel">
      <div className="yalola-card p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-4">
          {isSuper && <>
            <div><div className="text-2xl font-bold" style={{ fontFamily: "Outfit", color: "var(--yalola-primary)" }}>{qTotal || queue.length}</div><div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>En attente</div></div>
            <div><div className="text-2xl font-bold" style={{ fontFamily: "Outfit", color: "var(--yalola-secondary)" }}>{counts.verified}</div><div className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Vérifiées</div></div>
          </>}
        </div>
        {canManage && (
          <div className="flex items-center gap-2 flex-wrap">
            {isSuper && <>
              <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={doImport} data-testid="church-import-input" />
              <Button variant="outline" className="rounded-full" onClick={downloadTemplate} data-testid="church-import-template">
                <FileDown size={15} className="mr-1" /> Modèle CSV
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} disabled={importing} data-testid="church-import-btn">
                <Upload size={15} className="mr-1" /> {importing ? "Import…" : "Importer (CSV)"}
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => setOsmOpen(true)} data-testid="church-osm-btn">
                <Globe size={15} className="mr-1" /> Importer via OpenStreetMap
              </Button>
              <Button variant="outline" className="rounded-full" onClick={deleteDemo} disabled={demoBusy} data-testid="church-delete-demo-btn" style={{ color: "#DC2626", borderColor: "#DC262655" }}>
                <Trash2 size={15} className="mr-1" /> {demoBusy ? "Suppression…" : "Supprimer les églises démo"}
              </Button>
            </>}
            <Button className="pill-primary rounded-full" onClick={() => setEditItem(emptyChurch())} data-testid="church-add-btn">
              <Plus size={16} className="mr-1" /> Ajouter une église
            </Button>
          </div>
        )}
      </div>

      {/* Most-shared churches ranking */}
      {isSuper && topShared.some((c) => (c.share_views || 0) > 0) && (
        <div className="yalola-card p-4" data-testid="admin-top-shared">
          <div className="text-sm font-semibold mb-2 flex items-center gap-1.5" style={{ color: "var(--yalola-text)" }}>
            <Link2 size={15} /> Églises les plus partagées
          </div>
          <div className="space-y-1.5">
            {topShared.filter((c) => (c.share_views || 0) > 0).map((c, i) => (
              <div key={c.id} className="flex items-center justify-between text-sm gap-3">
                <span className="truncate" style={{ color: "var(--yalola-text-muted)" }}>
                  <span className="font-bold mr-1.5" style={{ color: "var(--yalola-secondary)" }}>{i + 1}.</span>
                  {c.name}{c.city ? <span className="opacity-60"> · {c.city}</span> : null}
                </span>
                <span className="font-bold tabular-nums shrink-0" style={{ color: "var(--yalola-primary)" }}>{c.share_views}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* GPS radius search settings ("Autour de moi") */}
      {isSuper && (
        <div className="yalola-card p-4" data-testid="church-geo-settings">
          <div className="flex items-center gap-2 mb-1">
            <LocateFixed size={16} style={{ color: "var(--yalola-primary)" }} />
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Recherche par géolocalisation (« Autour de moi »)</h3>
          </div>
          <p className="text-xs mb-3" style={{ color: "var(--yalola-text-muted)" }}>
            Règle le rayon par défaut utilisé quand un membre cherche les églises autour de sa position. Les églises doivent avoir des coordonnées GPS : elles sont géocodées automatiquement à l'ajout/validation, ou en masse via le bouton ci-dessous (OpenStreetMap, ~1 église/s).
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <label className="text-xs font-semibold" style={{ color: "var(--yalola-text-muted)" }}>Rayon par défaut (km)</label>
              <div className="flex gap-2 mt-1">
                <Input type="number" min={1} max={500} value={radiusKm} onChange={(e) => setRadiusKm(e.target.value)} className="w-28" data-testid="church-radius-input" />
                <Button className="pill-primary rounded-full" onClick={saveRadius} disabled={radiusBusy} data-testid="church-radius-save">{radiusBusy ? "…" : "Enregistrer"}</Button>
              </div>
            </div>
            <Button variant="outline" className="rounded-full" onClick={geocodeMissing} disabled={geocoding} data-testid="church-geocode-missing">
              <MapPin size={15} className="mr-1" /> {geocoding ? "Géocodage…" : "Géocoder les églises sans coordonnées"}
            </Button>
          </div>
          <div className="mt-3 pt-3 flex items-start justify-between gap-3 flex-wrap" style={{ borderTop: "1px solid var(--yalola-border)" }}>
            <div className="min-w-0">
              <div className="text-sm font-semibold">Mini-carte de proximité</div>
              <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Affiche une petite carte OpenStreetMap avec les directs en cours et les églises épinglés autour du membre (pages « Directs » et « Autour de moi »).</p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs font-semibold" style={{ color: mapEnabled ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }}>{mapEnabled ? "Activée" : "Désactivée"}</span>
              <Switch checked={mapEnabled} disabled={mapBusy} onCheckedChange={toggleNearbyMap} data-testid="nearby-map-toggle" />
            </div>
          </div>
        </div>
      )}

      {/* Demo/example churches toggle */}
      {isSuper && (
        <div className="yalola-card p-4 flex items-start justify-between gap-3 flex-wrap" data-testid="church-demo-settings">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <Trash2 size={16} style={{ color: "var(--yalola-primary)" }} />
              <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Églises de démonstration</h3>
            </div>
            <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
              Les églises d'exemple pré-remplies. Désactive pour les supprimer et éviter qu'elles ne reviennent au démarrage (utile en production). Tes vraies églises ne sont jamais touchées.
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-xs font-semibold" style={{ color: demoEnabled ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }}>{demoEnabled ? "Activées" : "Désactivées"}</span>
            <Switch checked={demoEnabled} disabled={demoBusy} onCheckedChange={toggleDemoMode} data-testid="church-demo-toggle" />
          </div>
        </div>
      )}

      {/* Pending suggestions with duplicate detection */}
      <div className="yalola-card p-4 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Suggestions en attente de validation</h3>
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>Vérifie, corrige si besoin, puis valide. Les doublons potentiels sont détectés automatiquement.</p>
        </div>
        {canManage && osmPendingCount > 0 && (
          <Button className="pill-primary rounded-full shrink-0" onClick={verifyAllOsm} disabled={verifyingAll} data-testid="church-verify-all-osm">
            <ShieldCheck size={15} className="mr-1" /> {verifyingAll ? "Validation…" : `Tout valider (OSM · ${osmPendingCount})`}
          </Button>
        )}
      </div>
      {queue.length === 0 && <div className="yalola-card p-8 text-center" data-testid="church-queue-empty" style={{ color: "var(--yalola-text-muted)" }}>Aucune église en attente.</div>}
      <div className="grid md:grid-cols-2 gap-3">
        {queue.map((c) => (
          <div key={c.id} className="yalola-card p-4" data-testid={`church-queue-${c.id}`}>
            <div className="font-semibold">{c.name}</div>
            <div className="text-xs flex items-center gap-1 mt-0.5" style={{ color: "var(--yalola-text-muted)" }}>
              <MapPin size={12} /> {c.city}, {c.country}{c.denomination ? ` · ${c.denomination}` : ""}
            </div>
            {(c.pastor_name || c.pastor_title) && (
              <div className="text-xs mt-1"><b>Responsable :</b> {[c.pastor_title, c.pastor_name].filter(Boolean).join(" ")}</div>
            )}
            {c.suggested_by_user && (
              <div className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }}>Suggérée par {c.suggested_by_user.name}</div>
            )}
            {c.description && <p className="text-xs mt-2">{c.description}</p>}
            {c.parent_church && (
              <span className="chip text-[10px] mt-2 inline-flex" style={{ background: "#EFF6FF", color: "#1E40AF" }} data-testid={`church-queue-parent-${c.id}`} data-proposed={c.parent_proposed_by ? 1 : 0}>
                <GitBranch size={10} /> Annexe de {c.parent_church.name}{c.parent_proposed_by ? " · proposée par le responsable (« Valider » applique le lien)" : ""}
              </span>
            )}

            {(c.duplicates || []).length > 0 && (
              <div className="mt-3 rounded-xl px-3 py-2.5 border" style={{ background: "#FEF3C7", borderColor: "#F59E0B" }} data-testid={`church-duplicates-${c.id}`}>
                <div className="flex items-center gap-1.5 text-xs font-bold" style={{ color: "#92400E" }}>
                  <AlertTriangle size={14} /> Doublon(s) potentiel(s) détecté(s)
                </div>
                <ul className="mt-1.5 space-y-1">
                  {c.duplicates.map((d) => (
                    <li key={d.id} className="text-[11px]" style={{ color: "#92400E" }}>
                      • <b>{d.name}</b> — {d.city}, {d.country}{d.verified ? " (vérifiée)" : " (en attente)"} — {(d.duplicate_reasons || []).join(", ")}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <LeaderChurchesBox church={c} canManage={canManage} onVerified={reloadAll} />
            <AskStatusChip church={c} />

            <div className="flex gap-2 mt-3 flex-wrap">
              <Button size="sm" className="pill-primary rounded-full" onClick={() => verify(c.id)} disabled={!canManage} data-testid={`church-verify-${c.id}`}><Check size={14} className="mr-1" /> Valider</Button>
              <Button size="sm" variant="outline" className="rounded-full" onClick={() => setEditItem({ ...emptyChurch(), ...c })} disabled={!canManage} data-testid={`church-edit-pending-${c.id}`}><Pencil size={13} className="mr-1" /> Modifier</Button>
              {(c.pastor_user_id || c.suggested_by) && (
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => setAskItem(c)} disabled={!canManage} title="Poser une question au responsable (ouvre un ticket support en son nom)" data-testid={`church-ask-${c.id}`}><MessageSquareText size={13} className="mr-1" /> Question au responsable</Button>
              )}
              {isSuper && <Button size="sm" variant="outline" className="rounded-full" onClick={() => remove(c)} data-testid={`church-del-${c.id}`}><X size={14} /></Button>}
            </div>
          </div>
        ))}
      </div>

      {qPages > 1 && (
        <div className="flex items-center justify-between gap-2 flex-wrap" data-testid="church-queue-pagination">
          <Button variant="outline" size="sm" className="rounded-full" disabled={qPage <= 1} onClick={() => loadQueue(qPage - 1)} data-testid="church-queue-prev">
            ← Précédent
          </Button>
          <span className="text-xs font-semibold tabular-nums" data-testid="church-queue-page-info">
            Page {qPage} / {qPages} · {qTotal.toLocaleString("fr-FR")} en attente
          </span>
          <Button variant="outline" size="sm" className="rounded-full" disabled={qPage >= qPages} onClick={() => loadQueue(qPage + 1)} data-testid="church-queue-next">
            Suivant →
          </Button>
        </div>
      )}

      {/* All churches with search */}
      <div className="yalola-card p-4">
        <h3 className="font-semibold mb-2" style={{ fontFamily: "Outfit" }}>Toutes les églises</h3>
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
            <Input className="pl-9" placeholder="Rechercher (nom, ville, responsable)..." value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadList(q)} data-testid="admin-church-search" />
          </div>
          <Button className="pill-primary rounded-full" onClick={() => loadList(q)} data-testid="admin-church-search-btn">Rechercher</Button>
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap text-xs">
          <button type="button" onClick={() => setFavFilter(!favOnly)} className={`chip ${favOnly ? "chip-primary" : ""}`} aria-pressed={favOnly} data-testid="admin-church-fav-filter" data-count={favCount}>
            <Star size={12} fill={favOnly ? "currentColor" : "none"} /> Mes favoris{favCount ? ` (${favCount})` : ""}
          </button>
          <span style={{ color: "var(--yalola-text-muted)" }}>Favoris personnels : visibles par toi seulement, indépendants de « À la une ».</span>
        </div>
        <div className="mt-3 divide-y" style={{ borderColor: "var(--yalola-border)" }}>
          {churches.map((c) => (
            <div key={c.id} className="flex items-center gap-3 py-2.5" data-testid={`admin-church-row-${c.id}`} data-favorite={favIds.has(c.id) ? "1" : "0"}>
              <button type="button" onClick={() => toggleFav(c)} className="shrink-0 h-8 w-8 rounded-full grid place-items-center hover:bg-black/5" title={favIds.has(c.id) ? "Retirer de mes favoris" : "Ajouter à mes favoris"} aria-label={favIds.has(c.id) ? "Retirer de mes favoris" : "Ajouter à mes favoris"} aria-pressed={favIds.has(c.id)} data-testid={`admin-church-fav-${c.id}`}>
                <Star size={16} style={{ color: favIds.has(c.id) ? "#E0A63C" : "var(--yalola-text-muted)" }} fill={favIds.has(c.id) ? "#E0A63C" : "none"} />
              </button>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate flex items-center gap-1.5">
                  {c.name}
                  {c.verified && <span className="chip text-[10px]" style={{ background: "var(--yalola-accent)22", color: "#8B6D28" }}><ShieldCheck size={10} /> Vérifiée</span>}
                  {c.parent_church_name && <span className="chip text-[10px]" style={{ background: "#EFF6FF", color: "#1E40AF" }} title="Annexe d'une église mère" data-testid={`admin-church-parent-chip-${c.id}`}><GitBranch size={10} /> Annexe de {c.parent_church_name}</span>}
                  {c.annexes_count > 0 && <span className="chip text-[10px]" style={{ background: "#EFF6FF", color: "#1E40AF" }} title="Église mère" data-testid={`admin-church-annexes-chip-${c.id}`}><GitBranch size={10} /> {c.annexes_count} annexe{c.annexes_count > 1 ? "s" : ""}</span>}
                  {!c.pastor_name && <span className="chip text-[10px]" style={{ background: "#FEF3C7", color: "#92400E" }} title="Ajoutez le responsable pour que l'auto-remplissage fonctionne à l'inscription" data-testid={`church-no-leader-${c.id}`}><AlertTriangle size={10} /> Responsable manquant</span>}
                </div>
                <div className="text-xs truncate" style={{ color: "var(--yalola-text-muted)" }}>
                  {c.city}, {c.country}{c.denomination ? ` · ${c.denomination}` : ""}{c.pastor_name ? ` · ${[c.pastor_title, c.pastor_name].filter(Boolean).join(" ")}` : ""}
                </div>
                {favIds.has(c.id) && (
                  <FavoriteNote churchId={c.id} note={favNotes[c.id] || ""} className="mt-0.5" onSaved={(n) => setFavNotes((m) => ({ ...m, [c.id]: n }))} />
                )}
              </div>
              {(canManage || canLink) && (
                <div className="flex gap-2 shrink-0 items-center">
                  {canLink && (
                    <button onClick={() => openLink(c)} title="Lier à un prédicateur" data-testid={`admin-church-link-${c.id}`}><UserPlus size={16} style={{ color: "var(--yalola-primary)" }} /></button>
                  )}
                  {canManage && (
                    <button onClick={() => setEditItem({ ...emptyChurch(), ...c })} title="Modifier" data-testid={`admin-church-edit-${c.id}`}><Pencil size={15} style={{ color: "var(--yalola-text-muted)" }} /></button>
                  )}
                  {isSuper && (
                    <button onClick={() => remove(c)} title="Supprimer" data-testid={`admin-church-delete-${c.id}`}><Trash2 size={15} style={{ color: "#DC2626" }} /></button>
                  )}
                </div>
              )}
            </div>
          ))}
          {churches.length === 0 && <div className="py-6 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-church-empty">{favOnly ? "Aucune église en favori — clique sur l'étoile d'une église pour la retrouver ici." : "Aucune église trouvée."}</div>}
        </div>
        {pages > 1 && (
          <div className="mt-3 flex items-center justify-between gap-2 flex-wrap" data-testid="church-pagination">
            <Button variant="outline" size="sm" className="rounded-full" disabled={page <= 1} onClick={() => loadList(q, page - 1)} data-testid="church-page-prev">
              ← Précédent
            </Button>
            <span className="text-xs font-semibold tabular-nums" data-testid="church-page-info">
              Page {page} / {pages} · {total.toLocaleString("fr-FR")} églises
            </span>
            <Button variant="outline" size="sm" className="rounded-full" disabled={page >= pages} onClick={() => loadList(q, page + 1)} data-testid="church-page-next">
              Suivant →
            </Button>
          </div>
        )}
      </div>

      {/* Recovery tool: attach an orphan live/replay to a church PAGE */}
      {canManage && (unattached.length > 0 || liveSearch) && (
        <div className="yalola-card p-4" data-testid="attach-live-tool">
          <div className="flex items-center gap-2 mb-1">
            <Radio size={16} style={{ color: "var(--yalola-primary)" }} />
            <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>Rattacher un direct à une page Église</h3>
          </div>
          <p className="text-xs mb-3" style={{ color: "var(--yalola-text-muted)" }}>Outil de secours : normalement, un responsable diffuse directement « en tant que » son église (pas besoin de rattacher). Les 3 directs non liés les plus récents sont affichés — recherche par titre ou animateur pour en retrouver un autre.</p>
          <div className="flex gap-2 mb-3">
            <div className="relative flex-1">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--yalola-text-muted)" }} />
              <Input className="pl-9" placeholder="Rechercher un direct (titre ou animateur)…" value={liveSearch} onChange={(e) => setLiveSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && loadUnattached(liveSearch.trim())} data-testid="attach-live-search" />
            </div>
            <Button className="pill-primary rounded-full" onClick={() => loadUnattached(liveSearch.trim())} data-testid="attach-live-search-btn">Rechercher</Button>
            {liveSearch && <Button variant="outline" className="rounded-full" onClick={() => { setLiveSearch(""); loadUnattached(); }} data-testid="attach-live-search-reset">Réinitialiser</Button>}
          </div>
          <div className="space-y-1">
            {unattached.length === 0 && (
              <div className="py-4 text-center text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="attach-live-empty">Aucun direct non lié trouvé.</div>
            )}
            {(() => { const hc = unattached.reduce((m, x) => { if (x.host_id) m[x.host_id] = (m[x.host_id] || 0) + 1; return m; }, {}); return unattached.map((l) => (
              <div key={l.id} className="flex items-center gap-2 flex-wrap py-2 border-b" style={{ borderColor: "var(--yalola-border)" }} data-testid={`unattached-live-${l.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">
                    {liveTitle(l, t, { name: l.host_name })} {l.status === "live" && <span className="chip text-[10px]" style={{ background: "#D9775722", color: "#D97757" }}>● live</span>}
                  </div>
                  <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{l.host_name ? `par ${l.host_name}` : ""}{l.started_at ? ` · ${new Date(l.started_at).toLocaleDateString("fr-FR")}` : ""}</div>
                </div>
                <select value={attachSel[l.id] || ""} onChange={(e) => setAttachSel({ ...attachSel, [l.id]: e.target.value })} className="h-9 rounded-md border px-2 text-sm bg-transparent" style={{ borderColor: "var(--yalola-border)" }} data-testid={`attach-church-select-${l.id}`}>
                  <option value="">Choisir une église…</option>
                  {churchOpts.map((c) => <option key={c.id} value={c.id}>{`${c.name} — ${c.city}`}</option>)}
                </select>
                <Button size="sm" className="pill-primary rounded-full" onClick={() => attachLive(l.id)} data-testid={`attach-live-btn-${l.id}`}><Link2 size={13} className="mr-1" /> Rattacher</Button>
                {l.host_id && hc[l.host_id] > 1 && (
                  <Button size="sm" variant="outline" className="rounded-full" onClick={() => attachAllFromHost(l.host_id, attachSel[l.id])} data-testid={`attach-all-host-btn-${l.id}`}><Link2 size={13} className="mr-1" /> Tout de {l.host_name || "cet hôte"} ({hc[l.host_id]})</Button>
                )}
              </div>
            )); })()}
          </div>
        </div>
      )}

      {/* Shared church form dialog (add + edit) */}
      <Dialog open={!!editItem} onOpenChange={(o) => !busy && !o && setEditItem(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editItem?.id ? "Modifier l'église" : "Ajouter une église"}</DialogTitle></DialogHeader>
          {editItem && (
            <div className="max-h-[60vh] overflow-y-auto pr-1">
              <ChurchForm value={editItem} onChange={setEditItem} testPrefix="admin-church-form" showMedia />
              <ParentChurchField value={editItem} onChange={setEditItem} />
              {editItem?.owner_id && !editItem?.owner_email && (
                <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>Un responsable est déjà lié à cette église. Saisissez un email pour le remplacer, ou laissez vide pour le conserver.</p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditItem(null)} disabled={busy}>Annuler</Button>
            <Button className="pill-primary" onClick={saveEdit} disabled={busy} data-testid="admin-church-form-save">{busy ? "Enregistrement..." : "Enregistrer"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {askItem && <AskResponsibleDialog church={askItem} onClose={() => setAskItem(null)} onSent={() => loadQueue(qPage)} />}
      {/* Dedicated privilege: link a church PAGE to a PREACHER account */}
      <Dialog open={!!linkItem} onOpenChange={(o) => !linkBusy && !o && setLinkItem(null)}>
        <DialogContent className="max-w-md" data-testid="admin-church-link-dialog">
          <DialogHeader><DialogTitle>Lier « {linkItem?.name} » à un prédicateur</DialogTitle></DialogHeader>
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
            Recherche un compte <b>Prédicateur</b> par nom ou email. Une fois lié, il pourra éditer la page de l'église (logo, couverture, description) et publier des annonces/stories en son nom.
          </p>
          {linkSel ? (
            <div className="flex items-center justify-between gap-2 rounded-xl border px-3 py-2" style={{ borderColor: "var(--yalola-primary)", background: "var(--yalola-primary-soft)" }} data-testid="admin-church-link-selected">
              <span className="text-sm"><b>{linkSel.name}</b> <span className="opacity-60">· {linkSel.email}</span></span>
              <button onClick={() => { setLinkSel(null); setLinkQ(""); }} className="opacity-60 hover:opacity-100" data-testid="admin-church-link-clear"><X size={14} /></button>
            </div>
          ) : (
            <div className="relative">
              <Input value={linkQ} onChange={(e) => searchPreachers(e.target.value)} placeholder="Rechercher un prédicateur (nom ou email)…" data-testid="admin-church-link-search" autoFocus />
              {linkResults.length > 0 && (
                <div className="mt-1 border rounded-xl divide-y max-h-56 overflow-y-auto" style={{ borderColor: "var(--yalola-border)" }} data-testid="admin-church-link-results">
                  {linkResults.map((u) => (
                    <button key={u.id} onClick={() => { setLinkSel(u); setLinkResults([]); }} className="w-full text-left px-3 py-2 text-sm hover:bg-[rgba(0,0,0,0.04)]" data-testid={`admin-church-link-result-${u.id}`}>
                      <span>{u.name}</span> <span className="opacity-50">· {u.email}</span>
                    </button>
                  ))}
                </div>
              )}
              {linkQ.trim().length >= 2 && linkResults.length === 0 && (
                <p className="text-xs mt-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="admin-church-link-noresult">Aucun prédicateur trouvé pour « {linkQ} ».</p>
              )}
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={unlinkOwner} disabled={linkBusy} data-testid="admin-church-unlink-btn">Délier</Button>
            <Button className="pill-primary" onClick={linkOwner} disabled={linkBusy || !linkSel} data-testid="admin-church-link-save">{linkBusy ? "..." : "Lier"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Find & import churches from OpenStreetMap (Nominatim + Overpass) */}
      <Dialog open={osmOpen} onOpenChange={(o) => !osmBusy && !osmImporting && setOsmOpen(o)}>
        <DialogContent className="max-w-2xl" data-testid="admin-church-osm-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Globe size={18} /> Importer des églises — OpenStreetMap</DialogTitle>
          </DialogHeader>
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>
            Recherche par zone (ville, région ou pays), puis import sélectif. Cochez les églises à ajouter : elles sont importées <b>en attente de validation</b> — vous les validez ensuite ci-dessus. Les églises déjà présentes sont grisées. Données © OpenStreetMap.
          </p>
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={osmZone} onChange={(e) => setOsmZone(e.target.value)} onKeyDown={(e) => e.key === "Enter" && runOsmSearch()} placeholder="Zone à rechercher (ex. Charleroi, Kinshasa, Belgique…)" className="flex-1" data-testid="osm-zone-input" autoFocus />
            <select value={osmCountry} onChange={(e) => setOsmCountry(e.target.value)} className="rounded-full border px-3 py-2 text-sm bg-transparent" style={{ borderColor: "var(--yalola-border)" }} data-testid="osm-country-select">
              <option value="">Pays (facultatif)</option>
              {countries.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
            <Button className="pill-primary rounded-full" onClick={runOsmSearch} disabled={osmBusy} data-testid="osm-search-btn">
              <Search size={15} className="mr-1" /> {osmBusy ? "Recherche…" : "Rechercher"}
            </Button>
          </div>
          {osmRes && (
            <>
              <div className="flex items-center justify-between text-xs" style={{ color: "var(--yalola-text-muted)" }}>
                <span data-testid="osm-result-summary">{osmRes.count} église(s) — {osmRes.zone_display}</span>
                {osmSelectable() > 0 && (
                  <button type="button" className="underline font-semibold" onClick={toggleOsmAll} data-testid="osm-select-all">
                    {osmSelected() < osmSelectable() ? "Tout sélectionner" : "Tout désélectionner"}
                  </button>
                )}
              </div>
              <div className="border rounded-xl divide-y max-h-[46vh] overflow-y-auto" style={{ borderColor: "var(--yalola-border)" }} data-testid="osm-results">
                {osmRes.churches.map((c) => (
                  <label key={c.osm_key} className={`flex items-start gap-3 px-3 py-2.5 text-sm ${c.already_exists ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-[rgba(0,0,0,0.03)]"}`} data-testid={`osm-item-${c.osm_key}`}>
                    <input type="checkbox" className="mt-1" disabled={c.already_exists} checked={!!osmSel[c.osm_key]} onChange={() => toggleOsm(c.osm_key)} data-testid={`osm-check-${c.osm_key}`} />
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold truncate">{c.name}</div>
                      <div className="text-xs flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }}>
                        <MapPin size={12} /> {[c.city, c.country].filter(Boolean).join(", ")}{c.denomination ? ` · ${c.denomination}` : ""}
                      </div>
                      {c.address && <div className="text-[11px]" style={{ color: "var(--yalola-text-muted)" }}>{c.address}</div>}
                    </div>
                    {c.already_exists && <span className="text-[10px] font-bold uppercase rounded-full px-2 py-0.5 shrink-0" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }}>Déjà présente</span>}
                  </label>
                ))}
              </div>
            </>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOsmOpen(false)} disabled={osmImporting}>Fermer</Button>
            {osmSelectable() > 0 && (
              <Button variant="outline" className="rounded-full" onClick={runOsmImportAll} disabled={osmImporting} data-testid="osm-import-all-btn">
                <Upload size={15} className="mr-1" /> {osmImporting ? "Import…" : `Tout importer (${osmSelectable()})`}
              </Button>
            )}
            <Button className="pill-primary" onClick={runOsmImport} disabled={osmImporting || osmSelected() === 0} data-testid="osm-import-btn">
              {osmImporting ? "Import…" : `Importer${osmSelected() ? ` (${osmSelected()})` : ""}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
